import { type FC, useEffect, useState } from "react";
import { motion } from "motion/react";
import {
  type TemplateCollection,
  TemplatesParser,
  PageContentProviderActive,
} from "@huub-web/templating";
import { useWebSocket } from "@huub-web/backend";
import { mergeClasses } from "@huub-web/utils";
import {
  Flex,
  Chip,
  ResponseBody,
  type ResponseVariant,
  Text,
  useFormattedDate,
  type ClassNameProp,
} from "@huub-web/ui";

import { useAppController } from "../../../hooks/useAppController";
import { responsesMotionProps } from "../StreamingAgentResponseController.motion";

import { messageStyle } from "./Response.css";

export type ResponseProps = ClassNameProp & {
  id: string;
  showTemplates?: boolean;
  showTime?: boolean;
  variant?: ResponseVariant;
  interactionLabel?: string;
  onChange?: () => void;
};

/**
 * Component that displays a chat response message.
 */
export const Response: FC<ResponseProps> = ({
  id,
  showTemplates = true,
  showTime = false,
  variant = "hub",
  interactionLabel,
  onChange,
  className,
}) => {
  const { agentResponses, isMessageStreaming } = useAppController();
  const { isWaitingForAgentResponse } = useWebSocket();
  const { formatDate } = useFormattedDate();

  const [body, setBody] = useState<string>(agentResponses?.[id]?.body ?? "");
  const [templates, setTemplates] = useState<TemplateCollection | null>(
    agentResponses[id]?.templates ?? [],
  );
  const [time] = useState<string>(
    formatDate(new Date(), { format: "minutesHours" }),
  );

  useEffect(() => {
    setBody(agentResponses?.[id]?.body ?? "");
    setTemplates(agentResponses?.[id]?.templates ?? []);
    onChange?.();
  }, [agentResponses]);

  return (
    <motion.div {...responsesMotionProps[variant]} data-id={id}>
      <Flex
        direction="column"
        className={mergeClasses(
          messageStyle({
            disabled: isWaitingForAgentResponse && !isMessageStreaming,
            variant,
          }),
          className,
        )}
        gap="s"
      >
        {showTime && time && (
          <Text variant="bodyXS" color="currentColor50" align="left">
            {time}
          </Text>
        )}

        {interactionLabel && (
          <Chip variant="tertiary" alignSelf="flex-start">
            {interactionLabel}
          </Chip>
        )}

        <ResponseBody
          variant={variant}
          isStreaming={isMessageStreaming}
          content={body}
        />

        {showTemplates && (templates?.length ?? 0) > 0 && (
          <PageContentProviderActive.Provider value={true}>
            <motion.div {...responsesMotionProps[variant]}>
              <TemplatesParser templates={templates ?? []} />
            </motion.div>
          </PageContentProviderActive.Provider>
        )}
      </Flex>
    </motion.div>
  );
};
